import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { styled } from '@mui/material/styles';
import MenuOutlinedIcon from '@mui/icons-material/MenuOutlined';
import NotificationsIcon from '@mui/icons-material/Notifications';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import PersonAddOutlinedIcon from '@mui/icons-material/PersonAddOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Toolbar from '@mui/material/Toolbar';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Avatar from '@mui/material/Avatar';
import Divider from '@mui/material/Divider';
import ListItemIcon from '@mui/material/ListItemIcon';

import MessageIcon from './MessageIcon';
import useAuth from 'hooks/useAuth';
import { useAppContext } from 'context/AppContextProvider';
import userImg from 'assets/images/users/user.jpg';

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  boxShadow: 'none',
  borderBottom: `1px solid ${theme.palette.grey[200]}`,
  transition: theme.transitions.create(['margin', 'width'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  [theme.breakpoints.up('lg')]: {
    width: '100%',
    marginLeft: 0,
  },
  [theme.breakpoints.down('lg')]: {
    width: '100%',
    marginLeft: 0,
  },
}));

const StyledToolbar = styled(Toolbar)(({ theme }) => ({
  minHeight: theme.mixins.toolbar.minHeight,
  padding: theme.spacing(0, 2),
  [theme.breakpoints.up('lg')]: {
    padding: theme.spacing(0, 3),
  },
}));

const VerticalDivider = styled('div')(({ theme }) => ({
  width: '1px',
  backgroundColor: theme.palette.grey[200],
  height: '25px',
  marginInline: theme.spacing(1),
}));

const StyledMenu = styled(Menu)(({ theme }) => ({
  '& .MuiMenu-paper': {
    width: '250px',
    right: 0,
    top: `${theme.mixins.toolbar.minHeight}px !important`,
    boxShadow: theme.shadows[3],
  },
}));

const AppHeader = ({ isDesktop }) => {
  const { authLogout } = useAuth();
  const { isSidebarOpen, setMobileSidebarOpen, logoutChat } = useAppContext();
  const [profileEl, setProfileEl] = useState(null);

  const toggleMobileSidebar = () => setMobileSidebarOpen(true);
  const handleProfileClick = (event) => setProfileEl(event.currentTarget);
  const handleProfileClose = () => setProfileEl(null);

  const handleLogout = async () => {
    try {
      await Promise.all([logoutChat(), authLogout()]);
      handleProfileClose();
    } catch (error) {
      toast.error(error?.message);
    }
  };

  return (
    <StyledAppBar position='fixed'>
      <StyledToolbar>
        <IconButton
          color='inherit'
          aria-label='menu'
          onClick={toggleMobileSidebar}
          sx={{
            display: { lg: 'none', xs: 'inline' },
            mr: 2,
          }}
        >
          <MenuOutlinedIcon />
        </IconButton>
        <Box flexGrow={1} />

        {/* Chat */}
        <MessageIcon />

        {/* Notifications */}
        <IconButton
          aria-label='notifications'
          color='inherit'
          aria-controls='notification-menu'
          aria-haspopup='true'
          onClick={() => {}}
        >
          <NotificationsIcon />
        </IconButton>

        <VerticalDivider />

        {/* Profile */}
        <IconButton
          aria-label='profile'
          color='inherit'
          aria-controls='profile-menu'
          aria-haspopup='true'
          onClick={handleProfileClick}
        >
          <Avatar
            src={userImg}
            alt='user-profile-img'
            sx={{ width: 32, height: 32 }}
          />
        </IconButton>

        <StyledMenu
          id='profile-menu'
          anchorEl={profileEl}
          keepMounted
          open={Boolean(profileEl)}
          onClose={handleProfileClose}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        >
          <MenuItem onClick={handleProfileClose}>
            <ListItemIcon>
              <AccountCircleIcon fontSize='small' />
            </ListItemIcon>
            My account
          </MenuItem>
          <Divider />
          <MenuItem onClick={handleProfileClose}>
            <ListItemIcon>
              <PersonAddOutlinedIcon fontSize='small' />
            </ListItemIcon>
            Add new user
          </MenuItem>
          <MenuItem onClick={handleProfileClose}>
            <ListItemIcon>
              <SettingsOutlinedIcon fontSize='small' />
            </ListItemIcon>
            Settings
          </MenuItem>
          <MenuItem onClick={handleLogout}>
            <ListItemIcon>
              <LogoutOutlinedIcon fontSize='small' />
            </ListItemIcon>
            Logout
          </MenuItem>
        </StyledMenu>
      </StyledToolbar>
    </StyledAppBar>
  );
};

export default AppHeader;
